import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import '../styles/LocationPicker.css';

// Fix pour les icônes par défaut de Leaflet avec Vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Composant pour gérer les clics sur la carte
function MapClickHandler({ onLocationSelect, position }) {
  const map = useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      // Validation des coordonnées
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        onLocationSelect({ lat, lng });
      }
    },
  });

  useEffect(() => {
    if (position) {
      map.flyTo([position.lat, position.lng], 10);
    }
  }, [position, map]);

  return null;
}

function LocationPicker({ onLocationSelect, initialLocation = null, isVisible = false }) {
  const [position, setPosition] = useState(initialLocation || { lat: 48.8566, lng: 2.3522 }); // Paris par défaut
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const mapRef = useRef(null);

  const handleLocationSelect = (newPosition) => {
    setPosition(newPosition);
    onLocationSelect(newPosition);
  };

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    try {
      // Utiliser l'API de géocodage Nominatim (gratuite)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const newPosition = {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon)
        };
        handleLocationSelect(newPosition);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche de localisation:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPosition = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          handleLocationSelect(newPosition);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
        }
      );
    }
  };

  if (!isVisible) return null;

  return (
    <div className="location-picker">
      <div className="location-picker-header">
        <h3>📍 Ajouter une localisation</h3>
        <p>Cliquez sur la carte ou recherchez un lieu</p>
      </div>

      {/* Barre de recherche */}
      <div className="location-search">
        <div className="search-input-container">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un lieu (ex: Paris, France)"
            className="location-search-input"
            onKeyPress={(e) => e.key === 'Enter' && searchLocation()}
          />
          <button 
            onClick={searchLocation} 
            disabled={isSearching}
            className="search-location-btn"
          >
            {isSearching ? '🔍' : '🔎'}
          </button>
        </div>
        
        {/* Bouton Ma position - affiché seulement après validation */}
        {position && position !== initialLocation && (
          <button 
            type="button"
            onClick={getCurrentLocation} 
            className="current-location-btn"
            title="Utiliser ma position actuelle"
          >
            📱 Ma position
          </button>
        )}
      </div>

      {/* Carte */}
      <div className="location-map-container">
        <MapContainer
          ref={mapRef}
          center={[position.lat, position.lng]}
          zoom={10}
          style={{ height: '300px', width: '100%', borderRadius: '12px' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapClickHandler 
            onLocationSelect={handleLocationSelect}
            position={position}
          />
          
          {position && (
            <Marker position={[position.lat, position.lng]} />
          )}
        </MapContainer>
      </div>

      {/* Coordonnées sélectionnées */}
      <div className="selected-coordinates">
        <p>
          <strong>Coordonnées:</strong> {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
        </p>
      </div>
    </div>
  );
}

export default LocationPicker;
